import { useEffect, useState } from "react";

/**
 * Tiny shared store for the SkAi dock/float state, used by both the global
 * SkAiDrawer (floating button) and the dashboard Organization Health section
 * (dock host). They live in separate component trees, so this module-level
 * pub/sub keeps them in sync.
 *
 *  popped      false = docked inside Organization Health, true = floating button
 *  x, y        floating position (null = default bottom-right corner)
 *  hostMounted true while the Organization Health dock host is on screen
 */
const STORAGE_KEY = "skaiDock";

// Restore the persisted popped/position across refreshes (hostMounted is
// runtime-only and always starts false).
const loadPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const { popped, x, y } = JSON.parse(raw) || {};
    return { popped: !!popped, x: x ?? null, y: y ?? null };
  } catch {
    return {};
  }
};

let state = { popped: false, x: null, y: null, hostMounted: false, ...loadPersisted() };
const listeners = new Set();

const persist = () => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ popped: state.popped, x: state.x, y: state.y })
    );
  } catch {
    /* ignore quota/availability errors */
  }
};

export const skaiDock = {
  get: () => state,
  set: (patch) => {
    state = { ...state, ...patch };
    // persist only when popped/position changed (not the runtime hostMounted flag)
    if ("popped" in patch || "x" in patch || "y" in patch) persist();
    listeners.forEach((l) => l(state));
  },
  subscribe: (l) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export function useSkaiDock() {
  const [snap, setSnap] = useState(state);
  useEffect(() => {
    setSnap(state); // re-sync in case state changed before we subscribed
    return skaiDock.subscribe(setSnap);
  }, []);
  return [snap, skaiDock.set];
}
